/**
 * Coordinate System Conversions
 * Uses proj4js for all CRS transformations.
 *
 * `CRS_OPTIONS` is the map readout (ribbon / status bar): a short list, including
 * UTM auto, which picks a zone from the cursor's longitude and latitude.
 * Column mapping must not use that list. Import uses `sourceCrsChoices` — every
 * WGS84 UTM zone, north and south — because easting/northing do not name a zone.
 * Guessing one (the old EPSG:32617 default) plots the data a zone or more away.
 */
import proj4 from 'proj4';
import { ensureCrs, normalizeCrs } from './georef';

// WGS84 is built-in as EPSG:4326. UTM zones (EPSG:32601–32660 and 32701–32760)
// are registered on demand by ensureCrs, including the southern false northing.
proj4.defs([
  ['EPSG:2236',  '+proj=tmerc +lat_0=24.33333333333333 +lon_0=-81 +k=0.999941177 +x_0=200000.0001016002 +y_0=0 +datum=NAD83 +units=us-ft +no_defs +type=crs'],
  ['EPSG:4269',  '+proj=longlat +datum=NAD83 +no_defs +type=crs'],
  ['EPSG:4978',  '+proj=geocent +datum=WGS84 +units=m +no_defs +type=crs'],
]);

/** Map-readout CRS list. Not the list of coordinate systems a file can be imported in. */
export const CRS_OPTIONS = [
  { code: 'EPSG:4326',  label: 'WGS84',            unit: '°',  decimals: 6 },
  { code: 'UTM_AUTO',  label: 'UTM (auto zone)',   unit: 'm',  decimals: 1 },
  { code: 'EPSG:32617', label: 'UTM 17N',           unit: 'm',  decimals: 1 },
  { code: 'EPSG:32614', label: 'UTM 14N',           unit: 'm',  decimals: 1 },
  { code: 'EPSG:2236',  label: 'State Plane FL E',  unit: 'ft', decimals: 1 },
  { code: 'EPSG:4269',  label: 'NAD83 (≈ WGS84)',   unit: '°',  decimals: 6 },
  { code: 'LOCAL',      label: 'Local grid',        unit: 'm',  decimals: 2 },
  { code: 'EPSG:4978',  label: 'ECEF',              unit: 'm',  decimals: 1 },
];

const SOURCE_EXTRAS = [
  { code: 'EPSG:4326', label: 'WGS84' },
  { code: 'UTM_AUTO', label: 'UTM (auto zone)' },
  { code: 'EPSG:2236', label: 'State Plane FL E' },
  { code: 'EPSG:4269', label: 'NAD83 (≈ WGS84)' },
  { code: 'LOCAL', label: 'Local grid' },
  { code: 'EPSG:4978', label: 'ECEF' },
];

/** Every WGS84 UTM zone, 1–60 north then 1–60 south. */
export function allUtmZones() {
  const zones = [];
  for (let zone = 1; zone <= 60; zone++) {
    zones.push({ code: `EPSG:${32600 + zone}`, label: `UTM ${zone}N`, zone, north: true });
  }
  for (let zone = 1; zone <= 60; zone++) {
    zones.push({ code: `EPSG:${32700 + zone}`, label: `UTM ${zone}S`, zone, north: false });
  }
  return zones;
}

function compact(s) {
  return String(s || '').trim().toLowerCase().replace(/\s+/g, '');
}

/**
 * Coordinate systems a file can be imported in. An empty query lists every UTM
 * zone so 10N and 16N are choices, not hidden behind a search. A query narrows
 * to that zone ("16", "10N", "UTM 56S") or a typed EPSG code.
 */
export function sourceCrsChoices(query = '') {
  const q = compact(query);
  const zones = allUtmZones();
  const zoneOk = (z) => {
    if (!q || q === 'utm' || q === 'utmzone' || q === 'zone') return true;
    const tag = `${z.zone}${z.north ? 'n' : 's'}`;
    if (q === tag || q === `utm${tag}` || q === `utmzone${tag}` || q === `zone${tag}`) return true;
    if (q === String(z.zone) || q === `utm${z.zone}` || q === `utmzone${z.zone}` || q === `zone${z.zone}`) return true;
    const digits = q.replace(/^epsg:?/, '');
    if (z.north && digits === String(32600 + z.zone)) return true;
    if (!z.north && digits === String(32700 + z.zone)) return true;
    return false;
  };
  const extraOk = (c) => {
    if (!q) return true;
    if (c.code === 'UTM_AUTO' && (q.includes('auto') || q === 'utm' || q === 'utmzone')) return true;
    return compact(`${c.label}${c.code}`).includes(q);
  };
  const list = [];
  const push = (item) => { if (!list.some((c) => c.code === item.code)) list.push(item); };
  if (!q) {
    push(SOURCE_EXTRAS[0]);
    push(SOURCE_EXTRAS[1]);
    zones.forEach(push);
    SOURCE_EXTRAS.slice(2).forEach(push);
  } else {
    SOURCE_EXTRAS.filter(extraOk).forEach(push);
    zones.filter(zoneOk).forEach(push);
  }
  const free = q.replace(/^epsg:?/, '');
  if (/^\d{4,6}$/.test(free)) push({ code: `EPSG:${free}`, label: `EPSG:${free}` });
  return list;
}

// Stated zone in a header, filename, or preamble. Mirrors crs_utils.utm_zone_from_text.
const EPSG_ZONE_RE = /(?:^|[^a-z0-9])epsg\s*:\s*(326|327)(\d{2})\b/i;
const UTM_ZONE_RE = /(?:^|[^a-z0-9])(?:utm(?:[_\s-]*zone)?|zone)[_\s-]*(\d{1,2})[_\s-]*([ns])\b/i;

/** UTM zone named in text, or null. Never infers a zone from easting or northing. */
export function utmZoneFromText(text) {
  const s = String(text ?? '');
  const epsg = EPSG_ZONE_RE.exec(s);
  if (epsg) {
    const zone = Number(epsg[2]);
    if (zone >= 1 && zone <= 60) {
      return { zone, north: epsg[1] === '326', code: `EPSG:${epsg[1]}${epsg[2]}`, source: 'epsg' };
    }
  }
  const named = UTM_ZONE_RE.exec(s);
  if (named) {
    const zone = Number(named[1]);
    if (zone >= 1 && zone <= 60) {
      const north = named[2].toUpperCase() === 'N';
      return { zone, north, code: `EPSG:${(north ? 32600 : 32700) + zone}`, source: 'header' };
    }
  }
  return null;
}

function finiteNums(values) {
  return (values || []).filter((n) => typeof n === 'number' && Number.isFinite(n));
}

function median(values) {
  const v = finiteNums(values).slice().sort((a, b) => a - b);
  if (!v.length) return NaN;
  const i = Math.floor(v.length / 2);
  return v.length % 2 ? v[i] : (v[i - 1] + v[i]) / 2;
}

/**
 * WGS84 UTM zone for a file. A stated EPSG or "UTM 16N" wins. Otherwise the
 * zone comes from a geographic location (longitude and latitude columns, or an
 * explicit anchor). Easting and northing are the same numbers in every zone, so
 * they are not a location and this returns null rather than zone 17.
 */
export function resolveUtmZone({ texts, lons, lats, anchor } = {}) {
  for (const text of texts || []) {
    const hit = utmZoneFromText(text);
    if (hit) return hit;
  }
  const lon = median(lons);
  const lat = median(lats);
  if (Number.isFinite(lon) && Number.isFinite(lat) && Math.abs(lon) <= 180 && Math.abs(lat) <= 90) {
    return { ...utmZoneFor(lon, lat), source: 'location' };
  }
  if (anchor && Number.isFinite(anchor.lon) && Number.isFinite(anchor.lat)
      && Math.abs(anchor.lon) <= 180 && Math.abs(anchor.lat) <= 90) {
    return { ...utmZoneFor(anchor.lon, anchor.lat), source: 'anchor' };
  }
  return null;
}

const EARTH_R = 6371008.8;

/** UTM zone/hemisphere for a WGS84 position. */
export function utmZoneFor(lon, lat) {
  const zone = Math.min(60, Math.max(1, Math.floor((((lon + 180) % 360) + 360) % 360 / 6) + 1));
  return { zone, north: lat >= 0, code: `EPSG:${(lat >= 0 ? 32600 : 32700) + zone}` };
}

/** Metres east/north of an origin ({lon, lat}) using a local equirectangular approximation. */
export function toLocalGrid(lon, lat, origin) {
  const o = origin && Number.isFinite(origin.lon) && Number.isFinite(origin.lat) ? origin : { lon, lat };
  const rad = Math.PI / 180;
  return {
    x: (lon - o.lon) * rad * EARTH_R * Math.cos(o.lat * rad),
    y: (lat - o.lat) * rad * EARTH_R,
  };
}

/**
 * Convert coordinates from WGS84 (lon, lat) to the target CRS.
 * `origin` ({lon, lat}) is only used by the LOCAL grid.
 * @param {number} lon - Longitude in WGS84
 * @param {number} lat - Latitude in WGS84
 * @param {string} targetCRS - EPSG code (e.g. 'EPSG:32617')
 * @returns {{ x: number, y: number, z?: number }}
 */
export function convertFromWGS84(lon, lat, targetCRS, origin) {
  if (targetCRS === 'LOCAL') {
    // Local grid — metres east/north of the project origin
    return toLocalGrid(lon, lat, origin);
  }
  const code = targetCRS === 'UTM_AUTO' ? utmZoneFor(lon, lat).code : normalizeCrs(targetCRS);
  if (!code || code === 'EPSG:4326' || code === 'local') {
    return { x: lon, y: lat };
  }
  try {
    if (!ensureCrs(code)) throw new Error(`CRS ${code} is not available`);
    const result = proj4('EPSG:4326', code, [lon, lat]);
    if (code === 'EPSG:4978') {
      return { x: result[0], y: result[1], z: result[2] || 0 };
    }
    return { x: result[0], y: result[1] };
  } catch (e) {
    console.warn('CRS conversion failed:', e);
    return { x: lon, y: lat };
  }
}

/**
 * Convert coordinates from a source CRS to WGS84 (lon, lat).
 * @param {number} x - easting / longitude in source CRS
 * @param {number} y - northing / latitude in source CRS
 * @param {string} sourceCRS - EPSG code (e.g. 'EPSG:32617')
 * @returns {{ lon: number, lat: number }}
 */
export function convertToWGS84(x, y, sourceCRS) {
  const code = normalizeCrs(sourceCRS);
  if (!code || code === 'EPSG:4326' || code === 'local') {
    return { lon: x, lat: y };
  }
  // Auto zone is a location, not a projection. Callers resolve it from the file
  // first (resolveUtmZone). Passing the token through used to return the raw
  // easting as a longitude, and every row was then dropped.
  if (code === 'UTM_AUTO') {
    throw new Error(
      'UTM auto zone needs a location in the file (longitude and latitude, or a zone such as UTM 16N). Easting and northing alone do not name a zone.',
    );
  }
  if (!ensureCrs(code)) {
    throw new Error(`CRS ${code} is not available for reprojection`);
  }
  const [lon, lat] = proj4(code, 'EPSG:4326', [x, y]);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
    throw new Error(`CRS ${code} did not produce a longitude and latitude`);
  }
  return { lon, lat };
}

/**
 * Format coordinates for display based on CRS type.
 * @param {number} lon - Longitude in WGS84
 * @param {number} lat - Latitude in WGS84
 * @param {string} crsCode - EPSG code
 * @returns {string} Formatted coordinate string
 */
export function formatCoordinates(lon, lat, crsCode, origin) {
  const crs = CRS_OPTIONS.find(c => c.code === crsCode) || CRS_OPTIONS[0];
  const { x, y, z } = convertFromWGS84(lon, lat, crsCode, origin);

  if (crsCode === 'EPSG:4326' || crsCode === 'EPSG:4269') {
    // Geographic — show as lat/lon with direction
    const latDir = y >= 0 ? 'N' : 'S';
    const lonDir = x >= 0 ? 'E' : 'W';
    return `${Math.abs(y).toFixed(crs.decimals)}°${latDir}, ${Math.abs(x).toFixed(crs.decimals)}°${lonDir}`;
  }

  if (crsCode === 'EPSG:4978') {
    // ECEF — show X, Y, Z
    return `X: ${x.toFixed(1)}  Y: ${y.toFixed(1)}  Z: ${(z || 0).toFixed(1)} m`;
  }

  if (crsCode === 'UTM_AUTO') {
    const u = utmZoneFor(lon, lat);
    return `${u.zone}${u.north ? 'N' : 'S'}  E: ${x.toFixed(crs.decimals)} m  N: ${y.toFixed(crs.decimals)} m`;
  }

  // Projected — show easting/northing
  return `E: ${x.toFixed(crs.decimals)} ${crs.unit}  N: ${y.toFixed(crs.decimals)} ${crs.unit}`;
}
